import SelectFileModal from '../../../SelectFileModal/SelectFileModal'
import { MainApi } from 'api/MainApi'
import { MissionRobotTypeOption, RobotApi } from 'api/RobotApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import SpeedSettingsSection from 'components/SpeedSettingsSection/SpeedSettingsSection'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import { useEffectOnce } from 'hooks/useEffectOnce'
import isEqual from 'lodash.isequal'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { MissionActions } from 'store/actions/mission'
import {
  selectGeoTiffLayer,
  selectMissionInfo,
  selectMissionTempVisualSettings,
  selectMissionVisualSettings,
} from 'store/selectors/mission'
import store from 'store/store'
import { layerThunks } from 'store/thunks/layer'
import { missionThunks } from 'store/thunks/mission'
import {
  DEFAULT_VIZUALIZATION_SETTINGS,
  FILES_LOADING_TYPE,
  GEOTIFF_LAYER_STATUSES,
  PROGRESS_TYPES,
} from 'utils/constants'
import { PATH } from 'utils/constants'

import style from './MissionSettingsModal.module.css'

interface IDialogProps {
  isOpen: boolean
  missionId: number
  missionName: string
  locationName?: string | null
  onClose: () => void
}

const MissionSettingsModal = ({
  isOpen,
  missionId,
  missionName,
  locationName,
  onClose,
}: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const inputFile = useRef<HTMLInputElement | null>(null)
  const missionInfo = useSelector(selectMissionInfo)
  const geoTiffLayer = useSelector(selectGeoTiffLayer)
  const visualSettings = useSelector(selectMissionVisualSettings)
  const tempVisualSettings = useSelector(selectMissionTempVisualSettings)

  const [tempLayerName, setTempLayerName] = useState(geoTiffLayer?.layer?.name || 'GeoTIFF layer not added')
  const [reloadNeeded, setReloadNeeded] = useState(false)
  const [tempMissionName, setTempMissionName] = useState(missionName)
  const [tempLocationName, setTempLocationName] = useState(locationName ? locationName : '')
  const [hasNameError, setNameHasError] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [actionInProgress, setActionInProgress] = useState(false)
  const [robotTypeApiOptions, setRobotTypeApiOptions] = useState<MissionRobotTypeOption[]>([])
  const [robotType, setRobotType] = useState(missionInfo?.robot_type || 'Sweeper')

  // Simple state for speeds - no need for complex useSpeedSettings
  const [cleaningSpeed, setCleaningSpeed] = useState(missionInfo?.cleaning_speed || 0.5)
  const [transportSpeed, setTransportSpeed] = useState(missionInfo?.transport_speed || 0.7)
  const [deleteMissionDialog, setDeleteMissionDialog] = useState(false)
  const navigate = useNavigate()

  const fetchData = async () => {
    try {
      setTempLocationName(locationName ? locationName : '')
      const opts = await RobotApi.fetchMissionRobotTypeOptions()
      if (opts.length > 0) setRobotTypeApiOptions(opts)
    } catch (error) {}
  }

  useEffectOnce(() => {
    fetchData()
  })

  const robotTypeOptions: IOption[] = useMemo(
    () => robotTypeApiOptions.map(opt => ({ text: opt.display_name, value: opt.robot_type })),
    [robotTypeApiOptions]
  )

  // Initialize data from frontend cache when dialog opens
  const prevIsOpen = useRef(isOpen)

  useEffect(() => {
    // Initialize only when dialog opens (transition from false to true)
    if (isOpen && !prevIsOpen.current) {
      fetchData()
      // Initialize speeds from frontend cache
      setCleaningSpeed(missionInfo?.cleaning_speed || 0.5)
      setTransportSpeed(missionInfo?.transport_speed || 0.7)

      // Initialize other fields
      setTempMissionName(missionName)
      setTempLocationName(locationName ? locationName : '')
      setNameHasError(false)
      const currentType = missionInfo?.robot_type || 'Sweeper'
      setRobotType(currentType)
      const matchedOption = robotTypeOptions.find(o => o.value === currentType)
      setRobotTypeOption(matchedOption || robotTypeOptions[0] || { text: currentType, value: currentType })

      // Initialize temporary states
      setTempLayerName(geoTiffLayer?.layer?.name || 'GeoTIFF layer not added')
      setReloadNeeded(false)
      setShowModal(false)
    }

    // Update previous value
    prevIsOpen.current = isOpen
  }, [
    isOpen,
    missionName,
    robotTypeOptions,
    geoTiffLayer?.layer?.name,
    missionInfo?.cleaning_speed,
    missionInfo?.transport_speed,
    missionInfo?.robot_type,
  ])

  const onCleaningSpeedChange = (speed: number) => setCleaningSpeed(speed)
  const onTransportSpeedChange = (speed: number) => setTransportSpeed(speed)

  const onMissionNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setNameHasError(true)
    } else {
      setNameHasError(false)
      setTempMissionName(e.target.value)
    }
  }

  const onLocationChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempLocationName(e.target.value)
  }

  const onLayerNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Layer name change handler - currently unused
  }

  const layerMessage = useMemo(() => {
    if (!geoTiffLayer.layer?.status) {
      return t('mission_settings.geotiff_description')
    }
    switch (geoTiffLayer.layer?.status) {
      case GEOTIFF_LAYER_STATUSES.ERROR:
        return t('mission_settings.geotiff_error')
      case GEOTIFF_LAYER_STATUSES.OK:
        return t('mission_settings.geotiff_status_ok')
      case GEOTIFF_LAYER_STATUSES.NEW:
        return t('mission_settings.geotiff_status_new')
      case GEOTIFF_LAYER_STATUSES.PENDING:
        return t('mission_settings.geotiff_status_pending')
    }
  }, [geoTiffLayer.layer?.status, t])

  const onCancelClicked = () => {
    if (!actionInProgress) {
      // Reset visual settings
      if (!isEqual(tempVisualSettings, visualSettings)) {
        dispatch(MissionActions.setTempVisualSettings({ ...visualSettings }))
      }

      // Reset temporary states
      setTempLayerName(geoTiffLayer?.layer?.name || 'GeoTIFF layer not added')
      setReloadNeeded(false)
      setShowModal(false)

      onClose()
    }
  }

  const onSaveClicked = async () => {
    setActionInProgress(true)

    try {
      if (tempMissionName !== missionInfo?.name) {
        const mission = await MainApi.changeMissionName(missionId, tempMissionName)
        if (mission.name) dispatch(MissionActions.setMissionName(mission.name))
      }

      if (robotType !== missionInfo?.robot_type) {
        const mission = await MainApi.changeMissionRobotType(missionId, robotType)
        if (mission.robot_type) dispatch(MissionActions.setMissionRobotType(mission.robot_type))
      }

      if (
        (tempLocationName !== '' || missionInfo?.location !== null) && // It is prevent an extra request because "" !== null
        tempLocationName !== missionInfo?.location
      ) {
        const mission = await MainApi.changeLocationName(missionId, tempLocationName === '' ? null : tempLocationName)
        if (mission.location) dispatch(MissionActions.setLocationName(mission.location))
      }

      if (transportSpeed !== missionInfo?.transport_speed || cleaningSpeed !== missionInfo?.cleaning_speed) {
        try {
          // Update mission speeds - backend will automatically update all zones
          const mission = await MainApi.changeMissionSpeed(missionId, transportSpeed, cleaningSpeed)

          if (mission.transport_speed && mission.cleaning_speed) {
            // Update mission speeds in frontend cache
            dispatch(MissionActions.setMissionTransportSpeed(mission.transport_speed))
            dispatch(MissionActions.setMissionCleaningSpeed(mission.cleaning_speed))

            // Update all zones in frontend cache with new speeds from backend response
            if (mission.cleaning_zones) {
              for (const zone of mission.cleaning_zones) {
                dispatch(
                  MissionActions.updateCleaningZoneSpeeds({
                    zoneId: zone.id,
                    cleaningSpeed: zone.cleaning_speed || cleaningSpeed,
                    transportSpeed: zone.transport_speed || transportSpeed,
                  })
                )
              }
            }
            store.dispatch(missionThunks.fetchMissionBackground(missionId))
          }
        } catch (error) {
          console.error('Error updating mission speeds:', error)
          throw error
        }
      }

      if (!isEqual(tempVisualSettings, visualSettings)) {
        const mission = await MainApi.changeMissionVisualSettings(missionId, tempVisualSettings)
        dispatch(
          MissionActions.setVisualSettings({ ...DEFAULT_VIZUALIZATION_SETTINGS, ...mission.visualization_settings })
        )
      }
    } catch (error) {
      console.log(error)
    }

    setActionInProgress(false)
    onClose()
    if (reloadNeeded) {
      setReloadNeeded(false)
      window.location.reload()
    }
  }

  const onDeleteMissionClick = () => {
    setDeleteMissionDialog(true)
  }

  const deleteMission = async () => {
    try {
      await MainApi.deleteMission(missionId)
      setDeleteMissionDialog(false)
      navigate(PATH.MISSION_LIST)
    } catch (error) {
      console.log('Error deleting mission:', error)
    }
  }

  const onDeleteLayerClick = () => {
    setTempLayerName('A GeoTIFF layer not added')
    if (geoTiffLayer.layer?.id) {
      store.dispatch(layerThunks.deleteLayer(geoTiffLayer.layer?.id))
    }
  }

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    event.preventDefault()
    const target = event.target
    const files = target?.files
    if (files && missionInfo?.id) {
      store.dispatch(layerThunks.createLayer(missionInfo?.id, tempLayerName, files[0]))
      event.target.value = ''
    }
  }

  const onServerFileSelected = () => {
    setShowModal(false)
  }

  const onSelectFromServerClick = () => {
    setShowModal(true)
  }

  const handleSelectFromServer = async (filename: string) => {
    setTempLayerName(filename)
    setReloadNeeded(true)
    if (missionInfo?.id) {
      store.dispatch(layerThunks.createLayerFromServer(missionInfo?.id, filename))
    }
  }

  const deletingLayerInProgress = geoTiffLayer.deletingProgress === PROGRESS_TYPES.WORK
  const uploadingLayerInProgress = geoTiffLayer.uploadingProgress === PROGRESS_TYPES.WORK
  const isSaveButtonDisabled =
    deletingLayerInProgress ||
    geoTiffLayer.layer?.status == GEOTIFF_LAYER_STATUSES.NEW ||
    geoTiffLayer.layer?.status == GEOTIFF_LAYER_STATUSES.PENDING ||
    (tempMissionName === '' && isEqual(visualSettings, tempVisualSettings))

  const [robotTypeOption, setRobotTypeOption] = useState(
    robotType ? { text: robotType, value: robotType } : robotTypeOptions[0]
  )

  const onTypeSelected = (e: IOption) => {
    setRobotTypeOption(e)
    setRobotType(e.value as string)
    setReloadNeeded(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCancelClicked}
      className={style.root}
      style={{
        overlay: {
          zIndex: 100,
          backgroundColor: 'rgba(27, 43, 72, 0.6)',
        },
      }}
      ariaHideApp={false}
    >
      <div className={style.modal}>
        <div className={style.head}>
          <Title text={t('mission_settings.title')} className={style.title} />
        </div>
        <div className={style.content}>
          <div className={style.row}>
            <TextFieldDescribed
              className={style.textfield}
              description={t('mission_settings.name_description')}
              error={t('mission_settings.name_error')}
              placeholder={t('mission_settings.name_placeholder')}
              hasError={hasNameError}
              defaultValue={missionName}
              onChange={onMissionNameChanged}
            />
          </div>
          {/* <div className={style.contentColumns}>
            <TextFieldDescribed
              className={style.rightMargin}
              description={t('mission_settings.buffer_description')}
              rightLabel={t('mission_settings.meters')}
              placeholder={t('mission_settings.name_placeholder')}
              defaultValue="25"
              onChange={() => {}}
            />
            <TextFieldDescribed
              description={t('mission_settings.start_point_max_distance')}
              rightLabel={t('mission_settings.meters')}
              placeholder={t('mission_settings.name_placeholder')}
              defaultValue="33"
              onChange={() => {}}
            />
          </div> */}
          <div className={style.row}>
            <Dropdown
              className={style.textfieldFullWidth}
              currentOption={robotTypeOption}
              options={robotTypeOptions}
              onClickOption={onTypeSelected}
              description={t('mission_settings.robot_type')}
            />
          </div>
          <div className={clsx(style.row, style.speedSelector)}>
            <SpeedSettingsSection
              cleaningSpeed={cleaningSpeed}
              transportSpeed={transportSpeed}
              onCleaningSpeedChange={onCleaningSpeedChange}
              onTransportSpeedChange={onTransportSpeedChange}
              disabled={false}
              showTransportSpeed={true}
              showCleaningSpeed={true}
              className={style.speedSelector}
            />
          </div>
          <div className={style.selectFileBlock}>
            <TextFieldDescribed
              className={style.textfieldFullWidth}
              description={t('mission_settings.geotiff_map_layer')}
              placeholder={t('mission_settings.name_placeholder')}
              value={tempLayerName}
              disabled={true}
              onChange={onLayerNameChanged}
            />
            {/* {!geoTiffLayer.layer && (
              <Button.Outlined
                className={style.btnSelect}
                onClick={onSelectFileClick}
                loading={uploadingLayerInProgress}
                disabled={uploadingLayerInProgress}
              >
                {uploadingLayerInProgress ? t('mission_settings.uploading_file') : t('mission_settings.select_file')}
              </Button.Outlined>
            )} */}
            {geoTiffLayer.layer && (
              <Button.Outlined
                className={style.btnSelect}
                onClick={onDeleteLayerClick}
                loading={deletingLayerInProgress}
                disabled={deletingLayerInProgress}
              >
                {deletingLayerInProgress ? t('mission_settings.deleting_layer') : t('mission_settings.delete_layer')}
              </Button.Outlined>
            )}
            {!geoTiffLayer.layer && (
              <Button.Outlined
                className={style.btnSelect}
                onClick={onSelectFromServerClick}
                loading={uploadingLayerInProgress}
                disabled={uploadingLayerInProgress}
              >
                Select
              </Button.Outlined>
            )}
            <input
              type="file"
              id="file"
              ref={inputFile}
              style={{ display: 'none' }}
              accept=" image/tif, .tif"
              multiple={false}
              onChange={onFileSelected}
            />
            <SelectFileModal
              isOpen={showModal}
              mode={FILES_LOADING_TYPE.MAP}
              onClose={() => setShowModal(false)}
              onConfirmClick={handleSelectFromServer}
            />
          </div>
          <div
            className={
              geoTiffLayer.layer?.status === GEOTIFF_LAYER_STATUSES.ERROR
                ? clsx(style.geotiffDescription, style.error)
                : style.geotiffDescription
            }
          >
            {layerMessage}
          </div>
          <div className={style.rowSpaceUp}>
            <TextFieldDescribed
              className={style.textfield}
              description={t('mission_settings.location_description')}
              error={t('mission_settings.location_error')}
              placeholder={t('mission_settings.location_placeholder')}
              hasError={false}
              defaultValue={tempLocationName}
              onChange={onLocationChanged}
            />
          </div>
          <Button.Outlined
            className={style.btnDelete}
            onClick={onDeleteMissionClick}
            loading={uploadingLayerInProgress}
            disabled={uploadingLayerInProgress}
          >
            Delete the mission
          </Button.Outlined>
          {/* <VisualSettings /> */}
        </div>
        <div className={style.footer}>
          <Button.Outlined className={style.btn} onClick={onCancelClicked} disabled={actionInProgress}>
            {t('mission_settings.label_cancel')}
          </Button.Outlined>
          <Button.Standart
            disabled={isSaveButtonDisabled || actionInProgress}
            loading={actionInProgress}
            className={style.btn}
            onClick={onSaveClicked}
          >
            {t('mission_settings.label_save')}
          </Button.Standart>
        </div>
      </div>
      <ConfirmDialog
        title={t('missions.delete_question')}
        isOpen={deleteMissionDialog}
        onClose={() => setDeleteMissionDialog(false)}
        onConfirmClick={deleteMission}
        confirmText={t('common.delete')}
        danger={true}
      />
    </Modal>
  )
}

export default MissionSettingsModal
